import { useEffect, useRef, useState } from "react";

/**
 * On mount it reads any previously-saved state from localStorage (keyed by
 * `tableKeyName`) so that navigating back from the user-view page restores the
 * last-used filters, page number, and search value.  The `qualificationsPageStateApply`
 * flag controls whether the stored state should actually be re-applied; after
 * reading it the flag is cleared so that a fresh page load always starts clean.
 *
 * When `locationStatus` is provided (e.g. navigated from a dashboard widget),
 * it overrides any stored filter and pre-selects the given qualification status.
 *
 * @param {string} tableKeyName - localStorage key for this user's table state.
 * @param {string|undefined} locationStatus - Pre-selected qualificationStatus from router state.
 * @returns {{ localStorageTableFilters: object, currentPage: number, currentSearchValue: string }}
 */
const useQualificationsPageState = (tableKeyName, locationStatus) => {
  const localStorageQualificationsApply = useRef(
    JSON.parse(localStorage.getItem("qualificationsPageStateApply")) || false
  );

  const localStorageQualificationsTable = useRef(
    localStorageQualificationsApply.current ? JSON.parse(localStorage.getItem("qualificationsPageState")) || {} : {}
  );

  const [currentPage, setCurrentPage] = useState(
    localStorage.getItem("smartTableLastPage") ? Number.parseInt(localStorage.getItem("smartTableLastPage"), 10) : 1
  );

  const [currentSearchValue, setCurrentSearchValue] = useState(
    localStorage.getItem("smartTableLastSearch") ? localStorage.getItem("smartTableLastSearch") : ""
  );

  // When navigating from a status-based deep-link (e.g. dashboard), apply that status
  // filter; otherwise restore from localStorage or use the default (valid contract,
  // all qualifications excluding previous function).
  const defaultFilters = locationStatus
    ? {
        anotherFilters: [
          { nameKey: "qualificationStatus", value: locationStatus },
          { nameKey: "validContract", value: 1 }
        ]
      }
    : {
        ...JSON.parse(localStorage.getItem(tableKeyName) || "{}"),
        anotherFilters: [
          { nameKey: "validContract", value: 1 },
          { nameKey: "qualificationStatus", value: "all_without_previous_function" }
        ]
      };

  const localStorageTableFilters = localStorageQualificationsApply.current
    ? {
        ...localStorageQualificationsTable.current.filters,
        anotherFilters: localStorageQualificationsTable.current.filters ?? [
          { nameKey: "qualificationStatus", value: locationStatus },
          { nameKey: "validContract", value: 1 }
        ]
      }
    : defaultFilters;

  // Clean up localStorage entries that were only meant for a single navigation
  useEffect(() => {
    const applyQualificationHistory = JSON.parse(localStorage.getItem("qualificationsPageStateApply")) || false;
    if (applyQualificationHistory === false) {
      localStorage.removeItem("qualificationsPageState");
      localStorage.removeItem("smartTableLastPage");
      localStorage.removeItem("smartTableLastSearch");
      setCurrentPage(1);
      setCurrentSearchValue("");
    }
    localStorage.removeItem("qualificationsPageStateApply");
  }, []);

  return { localStorageTableFilters, currentPage, currentSearchValue };
};

export default useQualificationsPageState;
